import { useDispatch, useSelector } from 'react-redux';
import { readinessActions as A } from '../../store/readinessSlice';
import { visibleQuestions } from '../../config/readinessQuestions';
import { buildAppSections } from '../../config/readinessAppSections';

const sectionKey = (sec) => (sec.id === 'program' ? 'programSpecific' : sec.id);

/**
 * Answer review screen: every assessment question with the answer given,
 * plus each full-application section with its saved values. Each section
 * offers "Pick up here" / "Update" to re-enter the conversation at that
 * point, with saved values prefilled.
 */
export default function ReviewScreen({ controller }) {
  const dispatch = useDispatch();
  const { answers, sections, selectedProgram, appDone } = useSelector((s) => s.readiness);

  const isRefi = answers.purpose === 'refinance';
  const qa = visibleQuestions(answers)
    .filter((q) => answers[q.id] !== undefined)
    .map((q) => {
      const v = answers[q.id];
      const opt = q.options && q.options.find((o) => o.v === v);
      return {
        id: q.id,
        question: (isRefi && q.textRefi) || q.text,
        answer: q.type === 'currency' ? '$' + v : opt ? opt.l : String(v),
      };
    });

  const secs = buildAppSections(answers, selectedProgram);
  const appStarted = appDone || Object.keys(sections).length > 0;
  const secStatus = secs.map((sec, i) => {
    const saved = sections[sectionKey(sec)];
    return {
      sec, i,
      status: !saved ? 'notStarted' : saved.skipped ? 'skipped' : 'saved',
      values: saved && !saved.skipped ? saved : {},
    };
  });
  const firstIncomplete = secStatus.findIndex((s) => s.status !== 'saved');

  return (
    <main data-screen-label="Answer review" className="rdz-results-main">
      <div className="rdz-results">
        <div>
          <button type="button" className="rdz-matches__back" onClick={() => dispatch(A.closeReview())}>
            &#8592; Back to my score
          </button>
          <h1 className="rdz-review__h1">Your questions &amp; answers</h1>
          <p className="rdz-programs__sub">
            Everything you&apos;ve shared with Owen so far. Nothing here goes to a lender until you
            choose to reveal yourself.
          </p>
        </div>

        {/* Assessment Q&A */}
        <div className="rdz-card">
          <h2 className="rdz-card__title">Readiness assessment</h2>
          <div className="rdz-review__qa-list">
            {qa.map((row) => (
              <div className="rdz-review__qa" key={row.id}>
                <div className="rdz-review__q">{row.question}</div>
                <div className="rdz-review__a">{row.answer}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Full application sections */}
        <div className="rdz-card">
          <h2 className="rdz-card__title">Full application &mdash; {selectedProgram || 'your program'}</h2>
          {!appStarted ? (
            <p className="rdz-card__sub">You haven&apos;t started the full application yet.</p>
          ) : (
            <div className="rdz-review__sections">
              {secStatus.map(({ sec, i, status, values }) => (
                <div className="rdz-review__section" key={sec.id + i}>
                  <div className="rdz-review__section-head">
                    <span className="rdz-review__section-title">{sec.title}</span>
                    <span className={'rdz-review__pill rdz-review__pill--' + status}>
                      {status === 'saved' ? 'Saved' : status === 'skipped' ? 'Skipped' : 'Not started'}
                    </span>
                    <button
                      type="button"
                      className="rdz-review__section-btn"
                      onClick={() => controller.resumeApp(i)}
                    >
                      {status === 'saved' ? 'Update' : 'Pick up here'}
                    </button>
                  </div>
                  {status === 'saved' && (
                    <div className="rdz-review__fields">
                      {sec.fields.filter((f) => f.type !== 'note').map((f) => (
                        <div className="rdz-review__field" key={f.k}>
                          <span className="rdz-review__field-label">{f.label}</span>
                          <span className="rdz-review__field-value">
                            {values[f.k]
                              ? (f.type === 'currency' ? '$' + values[f.k] : values[f.k])
                              : '\u2014'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resume CTA */}
        <div className="rdz-ctas">
          {!appStarted ? (
            <button
              type="button"
              className="rdz-btn-cta"
              onClick={() => { dispatch(A.closeReview()); controller.continueApp(); }}
            >
              Start my full application &mdash; {selectedProgram || ''}
            </button>
          ) : firstIncomplete >= 0 ? (
            <button
              type="button"
              className="rdz-btn-cta"
              onClick={() => controller.resumeApp(firstIncomplete)}
            >
              Pick up where I left off &mdash; {secStatus[firstIncomplete].sec.title}
            </button>
          ) : (
            <button type="button" className="rdz-btn-secondary" onClick={() => dispatch(A.closeReview())}>
              Everything&apos;s saved &mdash; back to my score
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
